<template>
  <v-card
    flat
    class="elevation-3"
    :data-cy="'content-upload-file-card-' + uploadable.file.name"
    :style="{
      'max-width':
        uploadable.subContentFiles.length > 0 ? 'fit-content' : '400px',
      opacity: subContentBeingDragged
        ? !(uploadable.isVideo || uploadable.isImage)
          ? 0.2
          : 1.0
        : 1.0,
    }"
  >
    <v-card-text>
      <!-- Remove Button -->
      <v-tooltip
        v-if="
          !subContentBeingDragged &&
            !disableControls &&
            !uploading &&
            !fullyUploaded
        "
        left
      >
        <v-btn
          slot="activator"
          class="ma-0 pa-0 elevation-0"
          style="
            position: absolute;
            top: 0px;
            right: 0px;
            min-width: 42px;
            min-height: 42px;
            z-index: 1;
          "
          @click="$emit('remove', uploadable)"
        >
          <v-icon medium>
            close
          </v-icon>
        </v-btn>
        <span>{{ $t('delete') }}</span>
      </v-tooltip>
      <!-- Filesize -->
      <div
        v-if="showFileSize && !subContentBeingDragged"
        class="pa-2 white--text title"
        style="
          position: absolute;
          top: 0px;
          left: 0px;
          height: 42px;
          display: flex;
          align-items: center;
          z-index: 1;
          background: rgba(0, 0, 0, 0.5);
        "
      >
        {{ formatFileSize(combinedVideosFileSize) }}
      </div>
      <v-layout
        justify-center
        align-center
        style="position: absolute; bottom: 8px; right: 8px; z-index: 1"
        :style="uploadFeedbackContainerCSS"
      >
        <!-- Upload Status Indicator -->
        <v-layout
          v-if="uploading && !fullyUploaded"
          column
          align-center
          justify-center
          style="min-width: 56px"
          :style="{'max-width': $vuetify.breakpoint.xs ? 'initial' : '60px'}"
        >
          <v-progress-circular
            :value="uploadProgress"
            color="primary"
          />
        </v-layout>
        <v-icon
          v-if="!uploading && fullyUploaded && !uploadFailed"
          class="success--text"
          large
        >
          check_circle
        </v-icon>
        <v-icon
          v-if="!uploading && fullyUploaded && uploadFailed"
          class="error--text"
          large
        >
          error_outline
        </v-icon>
      </v-layout>
      <v-layout column>
        <div
          :draggable="isDraggableContent"
          @dragstart="
            isDraggableContent ? onStartDragSubContent($event, file) : null
          "
          @dragend="isDraggableContent ? onEndDragSubContent() : null"
          @drop="onDropSubContent($event)"
          @dragover.prevent="onDragOverVideo($event)"
          @dragleave.prevent="onDragLeaveVideo($event)"
        >
          <!-- Preview -->
          <v-layout
            :key="file.lastModified"
            class="preview-container"
            :class="{
              'mini-preview': uploadable.subContentFiles.length === 0,
              contentdroptarget: isDraggableContentDropTarget,
              dragged:
                !!subContentBeingDragged && subContentBeingDragged === file,
            }"
          >
            <!-- Crop Image Button -->
            <div
              v-if="uploadable.isImage && imageNeedsToBeCroppedBeforeUpload"
              class="mt-2 overlayed-user-action-btn"
            >
              <v-btn
                fab
                color="warning"
                :title="$t('cropImage')"
                @click="showImageCropDialog = true"
              >
                <v-icon medium>
                  crop
                </v-icon>
              </v-btn>
            </div>
            <!-- Choose Video Format Button -->
            <div
              v-if="uploadable.isVideo && !videoHasCorrectAspectRatio"
              class="overlayed-user-action-btn"
            >
              <v-btn
                fab
                color="warning"
                :title="$t('chooseVideoFormats')"
                @click="showVideoFormatChooserDialog = true"
              >
                <v-icon medium>
                  dashboard
                </v-icon>
              </v-btn>
            </div>
            <!-- Image/Video Preview with SubContents support -->
            <div v-if="uploadable.isImage || uploadable.isVideo">
              <!-- Drop-Target Hint -->
              <v-fade-transition>
                <v-layout
                  v-if="
                    !!subContentBeingDragged &&
                      subContentBeingDragged !== file &&
                      isDraggableContentDropTarget
                  "
                  column
                  justify-center
                  align-center
                  class="contentdroptarget--hint mini-preview"
                >
                  <div class="title white--text">
                    <v-layout align-center>
                      <multiformat-bundle-indicator
                        is-multiformat-bundle
                        color-class="white--text"
                        class="mr-2"
                      />
                      {{ $t('contentUploadBundleSubContentDropHereHint') }}
                    </v-layout>
                  </div>
                  <div class="grey--text text--lighten-2 mt-3">
                    <div class="headline font-weight-bold">
                      {{ uploadable.title }}
                    </div>
                    <div class="title mt-1">
                      {{ file.name }}
                    </div>
                    <div
                      v-if="file.contentWidth && file.contentHeight"
                      class="mt-2 subheading"
                    >
                      {{ file.contentWidth }}x{{ file.contentHeight }}
                    </div>
                  </div>
                </v-layout>
              </v-fade-transition>
              <!-- Group Content Image Overlay Preview

              We overlay a container that mimics the 'object-fit:
              contain' logic and places the overlay image similar to how
              the backend will do it (the TVFormat specific tweaks are
              not applied here though).

              -->
              <group-content-overlay-image-preview
                v-if="contentOverlayImage && uploadable.file.containDimensions"
                :file="uploadable.file"
                :content-overlay-image="contentOverlayImage"
              />
              <!-- Image Preview -->
              <img
                v-if="uploadable.isImage && previewImage"
                class="lighter-background-checker-pattern mini-preview"
                :class="{clickable: isDraggableContent}"
                style="display: block; margin-left: auto; margin-right: auto"
                :src="previewImage"
                :title="
                  $t('preview') +
                    ': ' +
                    file.name +
                    (isDraggableContent
                      ? '\n' + $t('contentUploadBundleSubContentDragThisHint')
                      : '')
                "
                @load="checkImage({img: $event.target, file: file})"
              >
              <!--

              Modern Chrome versions (as well as Firefox seemingly?)
              can not preview all .mov/Quicktime files reliably (some
              work) - this seems to be rooted in the iPhone recording
              in HEVC aka H265 codec, which is not widely supported.
              However we can only check for proper resolution once the
              video has been loaded by the browser: No preview, no
              clientside validation. We try first and if we fail we do
              allow the upload since the server may be able to handle
              it or at least report back meaningful error messages.

              Note: v-show, not v-if! We need the element to exist so we
                can allow uploads of non-previewable videos where the
                User has chosen target formats.

              -->
              <!-- Video Preview -->
              <video
                v-show="previewUrl && previewVideoLoaded"
                ref="videopreview"
                :src="previewUrl"
                width="196px"
                class="
                  clickable
                  lighter-background-checker-pattern
                  mini-preview
                "
                :title="
                  $t('preview') +
                    ': ' +
                    file.name +
                    (isDraggableContent
                      ? '\n' + $t('contentUploadBundleSubContentDragThisHint')
                      : '')
                "
                @contextmenu.prevent
                @loadedmetadata="
                  uploadable.isVideo ? checkVideo(getVideoObject()) : null
                "
                @click.stop="togglePreview($refs.videopreview)"
                @ended="stopPreview($refs.videopreview)"
              />
              <!-- Sub Image/Video Files Previews -->
              <v-layout
                v-if="uploadable.subContentFiles.length > 0"
                align-center
                class="mt-2"
              >
                <multiformat-bundle-indicator
                  is-multiformat-bundle
                  color-class="info--text"
                  class="mr-2"
                />
                <span class="info--text">
                  &nbsp;{{ $t('contentUploadBundleSubContentHint') }}:
                </span>
                <v-btn
                  small
                  class="my-1 ml-auto mr-0"
                  flat
                  round
                  color="msr"
                  :title="$t('contentUploadBundleSubContentDisband')"
                  data-cy="subcontents-bundle-disband-btn"
                  :disabled="
                    disableControls ||
                      uploading ||
                      fullyUploaded ||
                      uploadFailed
                  "
                  @click="disbandSubContentBundle(file)"
                >
                  {{ $t('disband') }}
                </v-btn>
              </v-layout>
              <template
                v-for="(
                  subContentFile, subContentFileIndex
                ) in orderedSubContentFiles"
              >
                <!-- Sub Image Preview -->
                <img
                  v-if="subContentFile.type.startsWith('image')"
                  :key="'subimagepreview-' + subContentFileIndex"
                  class="lighter-background-checker-pattern mini-preview"
                  style="max-height: 200px"
                  :src="subContentFileNameToPreviewUrl[subContentFile.name]"
                >
                <!-- Sub Video Preview -->
                <video
                  v-else
                  :key="'subcontentpreview-' + subContentFileIndex"
                  ref="subcontentpreviews"
                  :src="subContentFileNameToPreviewUrl[subContentFile.name]"
                  width="260px"
                  style="max-height: 200px"
                  :title="$t('preview') + ': ' + subContentFile.name"
                  class="clickable ma-2"
                  data-cy="subcontent-preview"
                  @contextmenu.prevent
                  @loadedmetadata="setInitialPlayheadPosition($event.target)"
                  @click.stop="
                    togglePreview($refs.subcontentpreviews[subContentFileIndex])
                  "
                  @ended="
                    stopPreview($refs.subcontentpreviews[subContentFileIndex])
                  "
                />
              </template>
            </div>
            <div
              v-if="uploadable.isVideo && !previewVideoLoaded"
              class="text-xs-center"
            >
              <v-subheader style="font-size: 11px">
                {{ $t('noPreviewVideoPossible') }}
              </v-subheader>
            </div>
            <!-- Audio Preview -->
            <v-layout
              v-if="uploadable.isAudio && previewUrl"
              justify-center
              align-center
              class="mini-preview"
            >
              <mini-preview-audio
                :file-url="previewUrl"
                :closingtime="
                  selectedCategory && selectedCategory.is_closingtime
                "
                :style="{
                  transform:
                    'scale(' +
                    ($vuetify.breakpoint.smAndDown ? 1.5 : 2.5) +
                    ')',
                }"
              />
            </v-layout>
          </v-layout>
        </div>
        <!-- Title, dropdowns and other options -->
        <v-layout
          v-if="!subContentBeingDragged"
          column
          class="full-width mt-2"
        >
          <v-text-field
            v-model="uploadable.title"
            box
            class="v-text-custom-disabled mb-2"
            required
            :disabled="
              disableControls || !editableTitle || uploading || fullyUploaded
            "
            :hide-details="!showDetails"
            :title="$t('title')"
            :label="$t('title')"
            :rules="[rules.titleMin, rules.titleMax]"
            @keyup.esc="
              uploadable.title = '';
              $emit('update:showDetails', true);
            "
            @click:clear="
              uploadable.title = '';
              $emit('update:showDetails', true);
            "
          />
          <v-layout
            v-if="!uploadable.title"
            align-baseline
            justify-end
            class="mb-2"
          >
            <span class="mr-1">{{ $t('insertOriginalFilename') }}:</span>
            <a
              class="msr--text"
              href="javascript:void(0)"
              @click.stop="maybeSetTitleFromFileName(file.name)"
            >
              {{ getTitleFromFileName(file.name) }}
            </a>
          </v-layout>
          <!-- Category Dropdown -->
          <v-menu
            offset-y
            style="margin-left: -16px"
            :disabled="
              disableControls || !editableCategory || uploading || fullyUploaded
            "
          >
            <v-btn
              slot="activator"
              round
              flat
              class="ml-0 grey--text text--darken-3"
              :disabled="
                disableControls ||
                  !editableCategory ||
                  uploading ||
                  fullyUploaded
              "
            >
              <v-icon
                left
                color="msr"
              >
                folder
              </v-icon>
              <div
                v-if="selectedCategory"
                class="text-xs-left"
              >
                <div
                  class="caption"
                  style="font-size: 8px"
                >
                  {{ $t('category') }}
                </div>
                {{ selectedCategory.name }}
              </div>
            </v-btn>
            <v-list>
              <v-list-tile
                v-for="category in assignableCategories"
                :key="category.id"
                @click="uploadable.categoryId = category.id"
              >
                <v-list-tile-content>
                  <v-list-tile-title
                    :class="{
                      'font-weight-bold msr--text':
                        category.id == uploadable.categoryId,
                    }"
                  >
                    {{ category.name }}
                  </v-list-tile-title>
                </v-list-tile-content>
                <small
                  v-if="category.is_default"
                  class="grey--text ml-3"
                >{{
                  $t('default')
                }}</small>
                <small
                  v-else-if="category.group"
                  class="grey--text ml-3"
                >{{
                  category.group.name
                }}</small>
              </v-list-tile>
            </v-list>
          </v-menu>
          <v-layout
            align-center
            wrap
          >
            <!-- Dropdown to assign Tags -->
            <tag-select
              v-if="$userprofile && $userprofile.can_use_tagging"
              :button-icon-class="uploadable.tags.length > 0 ? 'msr--text' : ''"
              :button-label="$t('tags')"
              :disabled="disableControls || uploading || fullyUploaded"
              :show-extra-divider="unselectedTags.length > 0"
              :show-manage-tags-link="false"
              :tags="unselectedTags"
              :tag-entries-can-be-deactivated="tagEntriesCanBeDeactivated"
              button-text-class="grey--text text--darken-3"
              style="margin-left: -16px"
              @add="(tag) => addTagToSelection(tag)"
            >
              <div slot="extra-tile">
                <v-list-tile
                  class="grey--text text--darken-2"
                  style="text-transform: uppercase"
                  :disabled="disableControls || uploading || fullyUploaded"
                  @click="clearTagSelection()"
                >
                  {{ $t('clearTags') }}
                </v-list-tile>
              </div>
            </tag-select>
            <tag-select-list
              :tags="uploadable.tags"
              :removable="!disableControls && !uploading && !fullyUploaded"
              @remove="(tag) => removeTagFromSelection(tag)"
            />
          </v-layout>
          <!-- Modify Hard Range -->
          <hard-range-warning-list-tile
            v-if="!!uploadable.hardStart || !!uploadable.hardExpire"
            :hard-start="uploadable.hardStart"
            :hard-expire="uploadable.hardExpire"
            :chosen-period-feedback="false"
            :hide-when-fully-compatible="false"
            :hard-expire-offset="false"
            :disabled="disableControls || uploading || fullyUploaded"
          >
            <div
              slot="extra"
              class="nowrap"
            >
              <v-btn
                icon
                class="mr-0"
                small
                :title="$t('contentHardRangeSet')"
                @click="
                  disableControls || uploading || fullyUploaded
                    ? null
                    : (showEditHardRangeDialog = true)
                "
              >
                <v-icon
                  color="warning"
                  small
                >
                  edit
                </v-icon>
              </v-btn>
            </div>
          </hard-range-warning-list-tile>
          <div v-else>
            <v-layout
              align-center
              style="margin-left: -16px"
            >
              <v-btn
                round
                flat
                class="ml-0"
                :disabled="disableControls || uploading || fullyUploaded"
                @click="showEditHardRangeDialog = true"
              >
                <v-icon
                  left
                  color="grey darken-1"
                >
                  date_range
                </v-icon>
                <span
                  :class="{
                    'grey--text text--darken-3':
                      !disableControls && !uploading && !fullyUploaded,
                  }"
                >{{ $t('contentHardRangeSet') }}</span>
              </v-btn>
            </v-layout>
          </div>
          <!-- For Image: Choose Duration -->
          <v-text-field
            v-if="showDurationWidget"
            v-model="uploadable.duration"
            type="number"
            prepend-icon="av_timer"
            :min="minImageDuration"
            :max="maxImageDuration"
            step="1"
            class="mt-1"
            :style="{
              'max-width': $vuetify.breakpoint.smAndUp ? '100px' : 'initial',
            }"
            box
            :label="$t('seconds')"
            :title="$t('imageDurationSecondsTooltip')"
            :rules="[rules.duration]"
            :disabled="disableControls || uploading || fullyUploaded"
          />
          <!-- Optional Image Crop Option -->
          <div
            v-if="showOptionalImageCropOption"
            class="grey--text text--darken-1 pseudo-anchor nowrap mt-2"
            data-cy="choose-video-formats-optional-link"
            @click="showImageCropDialog = true"
          >
            {{ $t('cropImage') }}
          </div>
          <!-- Optional Video Format Chooser Link -->
          <div
            v-if="showOptionalVideoFormatChooseLink"
            class="grey--text text--darken-3 pseudo-anchor nowrap mt-2"
            data-cy="choose-video-formats-optional-link"
            @click="showVideoFormatChooserDialog = true"
          >
            {{ $t('chooseVideoFormats') }}
          </div>
        </v-layout>
      </v-layout>
      <!-- Feedback messages -->
      <div
        v-if="!uploading && showInfoMessage"
        class="info--text mt-2"
        style="width: 100%"
      >
        <v-layout align-center>
          <v-icon
            color="info"
            class="mr-2"
          >
            info
          </v-icon>
          <span
            v-if="uploadable.isAudio && selectedCategoryIsClosingtime"
            class="mr-3"
          >
            {{ $t('closingtimeCategorySelectedForUpload') }}
          </span>
          <span data-cy="content-upload-file-info-message">{{
            infoMessage
          }}</span>
        </v-layout>
      </div>
      <div
        v-if="!uploading && warningMessage"
        class="warning--text mt-2"
        :class="{clickable: !!warningMessageOnClick}"
        style="width: 100%"
        @click="!!warningMessageOnClick ? warningMessageOnClick() : null"
      >
        <v-layout align-center>
          <v-icon
            color="warning"
            class="mr-2"
          >
            warning
          </v-icon>
          <span :class="{'pseudo-anchor': !!warningMessageOnClick}">
            {{ warningMessage }}
          </span>
        </v-layout>
      </div>
      <div
        v-if="!uploading && fullyUploaded && uploadFailed"
        class="error--text mt-2"
        style="width: 100%"
      >
        <v-layout align-center>
          <v-icon
            color="error"
            class="mr-2"
          >
            error
          </v-icon>
          {{ failureMessage }}
        </v-layout>
      </div>
    </v-card-text>
    <!-- Image Crop Dialog -->
    <image-crop-dialog
      v-if="uploadable.isImage && imageWidth && imageHeight"
      :key="file.lastModified"
      :show.sync="showImageCropDialog"
      :image-filename="filename"
      :image-filepath="filepath"
      :image-width="imageWidth"
      :image-height="imageHeight"
      @saved="onCroppedImageSaved"
    />
    <!-- Video Format Chooser Dialog -->
    <video-format-chooser-dialog
      v-if="uploadable.isVideo"
      :show.sync="showVideoFormatChooserDialog"
      :mandatory="needToChooseVideoFormatBeforeUpload"
      :original-video-file="file"
      :last-chosen-tv-formats="uploadable.chosenTVFormats"
      @chosen="onVideoFormatsChosen"
    />
    <!-- Edit Hard Range Dialog -->
    <edit-hard-range-dialog
      :key="file.name + uploadable.hardStart + uploadable.hardExpire"
      :show.sync="showEditHardRangeDialog"
      :hard-expire="uploadable.hardExpire"
      :hard-start="uploadable.hardStart"
      :hard-expire-offset="false"
      @confirm="onHardRangeModified"
    />
  </v-card>
</template>

<script>
import {DEFAULT_SPOTCATEGORY_NAME} from '@/store';
import {sortVideoObjectsByPreferredFormatOrder} from '@/utils';

import {formatDate} from '@/mixins/timing';
import {IMAGE_DURATION_MAX, IMAGE_DURATION_MIN} from '@/mixins/imageDuration';
import {Uploadable, MAX_TOLERANCE_PERCENTAGE} from '@/pages/ContentUpload';

import EditHardRangeDialog from '@/components/EditHardRangeDialog';
import GroupContentOverlayImagePreview from '@/components/GroupContentOverlayImagePreview';
import HardRangeWarningListTile from '@/components/HardRangeWarningListTile';
import ImageCropDialog from '@/components/ImageCropDialog';
import MiniPreviewAudio from '@/components/MiniPreviewAudio';
import MultiformatBundleIndicator from '@/components/MultiformatBundleIndicator';
import TagSelect from '@/components/TagSelect';
import TagSelectList from '@/components/TagSelectList';
import VideoFormatChooserDialog from '@/components/VideoFormatChooserDialog';

const MAX_VIDEO_SIZE_MBS = 1024;

export default {
  components: {
    EditHardRangeDialog,
    GroupContentOverlayImagePreview,
    HardRangeWarningListTile,
    ImageCropDialog,
    MiniPreviewAudio,
    MultiformatBundleIndicator,
    TagSelect,
    TagSelectList,
    VideoFormatChooserDialog,
  },
  props: {
    uploadable: {
      type: Uploadable,
      required: true,
    },
    subContentBeingDragged: {
      type: [File, Blob],
      default: null,
    },
    categories: {
      type: Array[Object],
      required: true,
      validator(categories) {
        const empty = categories.length === 0;
        if (empty) {
          console.error('Must pass at least one SpotCategory');
        }
        return !empty;
      },
    },
    contentOverlayImage: {
      type: Object,
      default: null,
    },
    defaultCategory: {
      type: Object,
      default: null,
    },
    /** Allowed Tags, e.g. filtered by Group beforehand. */
    tags: {
      type: Array[Object],
      default: null,
    },
    tagEntriesCanBeDeactivated: {
      type: Boolean,
      default: true,
    },
    showFileSize: {
      type: Boolean,
      default: true,
    },
    showDetails: {
      type: Boolean,
      default: false,
    },
    showVideoFormatChooser: {
      type: Boolean,
      default: true,
    },
    disableControls: {
      type: Boolean,
      default: false,
    },
    editableTitle: {
      type: Boolean,
      default: true,
    },
    editableCategory: {
      type: Boolean,
      default: true,
    },
    editableHardRange: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showEditHardRangeDialog: false,

      uploading: false,
      uploadProgress: 0,
      fullyUploaded: false,
      uploadFailed: false,
      failureMessage: this.$t('genericFailFeedback'),
      warningMessage: '',
      warningMessageOnClick: null,
      infoMessage: '',

      previewingVideoElement: null,
      previewImage: null,
      previewUrl: null,

      // Whether the <video> has loaded the file successfully and
      // detected a valid resolution. True initially so the <video> is
      // rendered, but removes it in favor of a placeholder text if the
      // loading fails.
      previewVideoLoaded: true,

      // Image specific fields.
      // Updated once the image is loaded in the <img> tag.
      imageWidth: 0,
      imageHeight: 0,
      minImageDuration: IMAGE_DURATION_MIN,
      maxImageDuration: IMAGE_DURATION_MAX,
      imageHasCorrectAspectRatio: false,
      showImageCropDialog: false,

      // Video specific fields.
      // Updated once the video's metadata is loaded in the <video> tag.
      videoHasCorrectAspectRatio: false,
      isConvertibleMultiscreen_: false,
      isSpecialLedWallFormat_: false,
      showVideoFormatChooserDialog: false,

      titleLimit: 128,
      rules: {
        titleMin: (value) => {
          return (value || '').trim().length > 0 || this.$t('titleTooShort');
        },
        titleMax: (value) => {
          return (
            (value || '').length <= this.titleLimit || this.$t('titleTooLong')
          );
        },
        duration: (value) => {
          if (value < this.minImageDuration) {
            return this.$t('tooShort');
          }
          if (value > this.maxImageDuration) {
            return this.$t('tooLong');
          }
          return true;
        },
      },
    };
  },
  computed: {
    /**
     * Return subContentFiles in preferred order based on video aspect ratio.
     */
    orderedSubContentFiles() {
      return sortVideoObjectsByPreferredFormatOrder(
        this.uploadable.subContentFiles
      );
    },
    subContentFileNameToPreviewUrl() {
      const fileNameToUrl = {};
      for (const subContentFile of this.uploadable.subContentFiles) {
        fileNameToUrl[subContentFile.name] =
          this.makeVideoFilePreviewUrl(subContentFile);
      }
      return fileNameToUrl;
    },
    combinedVideosFileSize() {
      function addUp(accumulator, curr) {
        return accumulator + curr;
      }
      const sizes = [
        this.file.size,
        ...this.uploadable.subContentFiles.map(
          (subContentFile) => subContentFile.size
        ),
      ];
      return sizes.reduce(addUp);
    },
    hasImageDimensions() {
      return this.imageWidth > 0 && this.imageHeight > 0;
    },
    isDraggableContent() {
      return (
        (this.uploadable.isVideo ||
          (this.uploadable.isImage &&
            this.hasImageDimensions &&
            this.imageHasCorrectAspectRatio)) &&
        this.uploadable.subContentFiles.length === 0 &&
        this.uploadable.chosenTVFormats.length === 0 &&
        !this.needToChooseVideoFormatBeforeUpload
      );
    },
    /**
     * Prevent dropping when already chosen formats or dragged format already used.
     *
     * Bundling would fail with same video formats which we can
     * estimate via their aspect ratios, prevent adding in that case.
     * This is a guess at best, we don't know yet what TVFormats the
     * backend would make from it.
     *
     * The dimensions on the File object are augmented inside
     * checkVideo() after loadedmetadata.
     */
    isDraggableContentDropTarget() {
      if (
        !(
          this.uploadable.isVideo ||
          (this.uploadable.isImage &&
            this.hasImageDimensions &&
            this.imageHasCorrectAspectRatio)
        ) ||
        this.uploadable.chosenTVFormats.length > 0 ||
        this.needToChooseVideoFormatBeforeUpload
      ) {
        return false;
      }

      if (
        this.subContentBeingDragged &&
        this.subContentBeingDragged.contentWidth &&
        this.subContentBeingDragged.contentHeight
      ) {
        const ratio = (
          this.subContentBeingDragged.contentWidth /
          this.subContentBeingDragged.contentHeight
        ).toFixed(2);
        for (const existingContentFile of [
          this.file,
          ...this.uploadable.subContentFiles,
        ]) {
          if (
            !existingContentFile.contentWidth ||
            !existingContentFile.contentHeight
          ) {
            continue;
          }
          const existingRatio = (
            existingContentFile.contentWidth / existingContentFile.contentHeight
          ).toFixed(2);
          if (ratio === existingRatio) {
            return false;
          }
        }
      }

      return !!this.subContentBeingDragged;
    },
    unselectedTags() {
      const selectedTagIds = this.uploadable.tags.map((t) => t.id);
      return this.tags.filter((tag) => !selectedTagIds.includes(tag.id));
    },
    nonClosingtimeCategories() {
      return this.categories.filter((category) => !category.is_closingtime);
    },
    assignableCategories() {
      if (this.uploadable.isAudio) {
        return this.categories;
      }
      return this.nonClosingtimeCategories;
    },
    selectedCategory() {
      if (!this.uploadable.categoryId) {
        return null;
      }
      const matches = this.categories.filter(
        (category) => category.id === this.uploadable.categoryId
      );
      return matches.length ? matches[0] : null;
    },
    selectedCategoryIsClosingtime() {
      return this.selectedCategory && this.selectedCategory.is_closingtime;
    },
    imageNeedsToBeCroppedBeforeUpload() {
      if (
        !this.uploadable.isImage ||
        this.uploadable.subContentFiles.length > 0
      ) {
        return false;
      }
      return !this.imageHasCorrectAspectRatio;
    },
    needToChooseVideoFormatBeforeUpload() {
      if (!this.uploadable.isVideo) {
        return false;
      }
      return !this.videoHasCorrectAspectRatio || !this.previewVideoLoaded;
    },
    showOptionalImageCropOption() {
      return (
        this.uploadable.isImage &&
        !this.uploadable.subContentFiles.length &&
        !!this.imageHasCorrectAspectRatio &&
        !this.uploading &&
        !this.fullyUploaded
      );
    },
    showOptionalVideoFormatChooseLink() {
      return (
        this.showVideoFormatChooser &&
        this.uploadable.isVideo &&
        !this.uploadable.subContentFiles.length &&
        !this.needToChooseVideoFormatBeforeUpload &&
        !this.isConvertibleMultiscreen_ &&
        !this.isSpecialLedWallFormat_ &&
        !this.uploading &&
        !this.fullyUploaded
      );
    },
    showInfoMessage() {
      return !!this.infoMessage || this.selectedCategoryIsClosingtime;
    },
    showDurationWidget() {
      return (
        this.uploadable.isImage ||
        this.uploadable.subContentFiles.some((subContentFile) =>
          subContentFile.type.startsWith('image')
        )
      );
    },
    uploadFeedbackContainerCSS() {
      const mobile = this.$vuetify.breakpoint.xs;
      let maxWidth = mobile ? 'initial' : '60px';
      // if (this.uploading && !mobile) {
      // 	maxWidth = '60px';  // Give some space for the circular spinner.
      // }
      return {
        'max-width': maxWidth,
      };
    },
    /** Note: Fetched in the parent ContentUploadDialog once. */
    specialTVFormatsAllowedToUpload() {
      return this.$store.state.common.specialTVFormatsAllowedToUpload;
    },
    file() {
      if (!this.uploadable) {
        return null;
      }
      return this.uploadable.file;
    },
    filename() {
      return this.file.name;
    },
    filepath() {
      return URL.createObjectURL(this.uploadable.file);
    },
    /**
     * The backend can only handle 178956970 pixels at most, check this.
     *
     * This is a limitation of PIL.Image that will raise a
     * DecompressionBombError for very large images resolutions, e.g.
     * 22677x12756 px.
     */
    imageIsTooLarge() {
      if (!this.uploadable.isImage) {
        return false;
      }
      const maxPixels = 178956970;
      return this.imageWidth * this.imageHeight > maxPixels;
    },
    imageIsTooSmall() {
      if (!this.uploadable.isImage) {
        return false;
      }
      const minPixels = 1000;
      return this.imageWidth < minPixels || this.imageHeight < minPixels;
    },
  },
  mounted() {
    this.initializeUploadable();
  },
  methods: {
    /**
     * Calculate dimensions to contain input image within target image.
     *
     * Width is matched first, then height is scaled accordingly to
     * fully be contained the target. Positions are updated to center
     * the contain image.
     *
     * Args:
     *   - inputWidth (Number)
     *   - inputHeight (Number)
     *   - targetWidth (Number)
     *   - targetHeight (Number)
     *
     * Returns:
     *   Object like {containWidth: 100, containHeight: 100, xOffset: 50, yOffset: 50}
     */
    calculateContainDimensions(
      inputWidth,
      inputHeight,
      targetWidth,
      targetHeight
    ) {
      const inputAspectRatio = inputWidth / inputHeight;

      let containWidth = targetWidth;
      let containHeight = Math.round(containWidth / inputAspectRatio);

      if (containHeight > targetHeight) {
        containHeight = targetHeight;
        containWidth = Math.round(containHeight * inputAspectRatio);
      }

      const xOffset = Math.abs(Math.round((targetWidth - containWidth) / 2.0));
      const yOffset = Math.abs(
        Math.round((targetHeight - containHeight) / 2.0)
      );

      return {containWidth, containHeight, xOffset, yOffset};
    },
    /**
     * Setup a link between the Uploadable object and this component.
     *
     * This is not reactive and will have to be updated e.g. after
     * bundling/disbanding.
     */
    initializeUploadableRef() {
      this.uploadable.ref = this;
    },
    initializeUploadable() {
      this.initializeUploadableRef();
      this.uploadable.categoryId = this.getInitialCategoryId();
      this.maybeSetTitleFromFileName(this.file.name);

      this.validateFileTypeSpecified();
      this.createPreview(this.file);
    },
    validateFileTypeSpecified() {
      const noTypeSpecified =
        !this.uploadable.isImage &&
        !this.uploadable.isVideo &&
        !this.uploadable.isAudio;
      if (noTypeSpecified) {
        throw 'File must be marked as being either image, video or audio!';
      }
    },
    disbandSubContentBundle(file) {
      this.$emit('sub-content-bundle-disband', file);
    },
    onStartDragSubContent(event, file) {
      event.dataTransfer.dropEffect = 'move';
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('draggedFileName', file.name);

      this.$emit('update:subContentBeingDragged', file);
    },
    onEndDragSubContent() {
      this.$emit('update:subContentBeingDragged', null);
    },
    onDragOverVideo(event) {
      event.target.classList.add('over');
    },
    onDragLeaveVideo(event) {
      event.target.classList.remove('over');
    },
    onDropSubContent(event) {
      event.target.classList.remove('over');
      this.$emit('update:subContentBeingDragged', null);

      if (this.isDraggableContentDropTarget) {
        const draggedFileName = event.dataTransfer.getData('draggedFileName');
        this.$emit('sub-content-dropped', draggedFileName);
      }

      event.stopPropagation();
      return false;
    },
    getInitialCategoryId() {
      if (this.defaultCategory) {
        return this.defaultCategory.id;
      }

      const byName = this.categories.filter(
        (category) =>
          category.is_default && category.name === DEFAULT_SPOTCATEGORY_NAME
      )[0];
      if (byName) {
        return byName.id;
      }

      return this.categories[0].id;
    },
    addTagToSelection(tag) {
      for (const existingTag of this.uploadable.tags) {
        if (existingTag.id === tag.id) {
          return;
        }
      }
      this.uploadable.tags.push(tag);
      this.$emit('tag-added', tag);
    },
    removeTagFromSelection(tag) {
      const index = this.uploadable.tags.findIndex((t) => t.id === tag.id);
      if (index >= 0) {
        this.uploadable.tags.splice(index, 1);
      }
      this.$emit('tag-removed', tag);
    },
    clearTagSelection() {
      // We do this one by one on purpose so an event is emitted for each.
      // Inverted order to not conflict with the indexed .splice() above.
      for (let index = this.uploadable.tags.length - 1; index >= 0; index--) {
        const tag = this.uploadable.tags[index];
        this.removeTagFromSelection(tag);
      }
    },
    onHardRangeModified(opts) {
      this.uploadable.hardStart = opts.newHardStart;
      let newHardExpire = opts.newHardExpire;
      if (newHardExpire) {
        newHardExpire = formatDate(opts.newHardExpire);
      }
      this.uploadable.hardExpire = newHardExpire;
    },
    hasSupportedDimensions(width, height) {
      // See: msrcms.utils.media.validate_resolution_for_client_upload()
      const aspect = Number(width / height);

      // How much deviation from ideal ratios we tolerate is based on
      // trial and error and hardcoded here as a percentage. Note: This
      // must be the same value as used in the backend.
      const landscapeToleranceValue =
        (16.0 / 9.0 / 100.0) * MAX_TOLERANCE_PERCENTAGE;
      const portraitToleranceValue =
        (9.0 / 16.0 / 100.0) * MAX_TOLERANCE_PERCENTAGE;

      const landscapeOffset = Math.abs(16.0 / 9.0 - aspect);
      const portraitOffset = Math.abs(9.0 / 16.0 - aspect);

      const formatWithinBounds =
        landscapeOffset < landscapeToleranceValue ||
        portraitOffset < portraitToleranceValue;

      console.log('[DEBUG] Offset to landscape is: ' + landscapeOffset);
      console.log('[DEBUG] Offset to portrait is: ' + portraitOffset);
      console.log('[DEBUG] Format is within tolerance: ' + formatWithinBounds);

      if (formatWithinBounds) {
        return true;
      }

      return this.isSpecialAllowedTVFormat(width, height);
    },
    isSpecialAllowedTVFormat(width, height) {
      for (let tvformat of this.specialTVFormatsAllowedToUpload) {
        if (tvformat.width === width && tvformat.height === height) {
          return true;
        }
      }
      return false;
    },
    isConvertibleMultiscreen(width, height) {
      for (let tvformat of this.specialTVFormatsAllowedToUpload) {
        if (tvformat.width === width && tvformat.height === height) {
          return tvformat.is_convertible_multiscreen;
        }
      }
      return false;
    },
    isSpecialLedWallFormat(width, height) {
      for (let tvformat of this.specialTVFormatsAllowedToUpload) {
        if (tvformat.width === width && tvformat.height === height) {
          return tvformat.is_ledwall_format;
        }
      }
      return false;
    },
    checkImage(data) {
      if (!data.img) {
        return;
      }

      // Validate and add warnings as needed.
      this.warningMessage = '';
      this.warningMessageOnClick = null;
      let warning = '';

      const videoMbs = data.file.size / 1000.0 / 1000.0;
      if (videoMbs > MAX_VIDEO_SIZE_MBS) {
        warning += this.$t('videoUploadSizeWarning') + '. ';
      }

      this.imageWidth = data.img.naturalWidth;
      this.imageHeight = data.img.naturalHeight;
      if (!this.hasSupportedDimensions(this.imageWidth, this.imageHeight)) {
        this.imageHasCorrectAspectRatio = false;
        warning += this.$t('cropImagePlease') + '. ';
        this.warningMessageOnClick = function () {
          this.showImageCropDialog = true;
        };
      } else {
        this.imageHasCorrectAspectRatio = true;
      }
      if (this.imageIsTooLarge) {
        warning += ' ' + this.$t('imageTooLargeForUpload');
      } else if (this.imageIsTooSmall) {
        warning += ' ' + this.$t('imageTooSmallForUpload');
      }

      if (warning) {
        this.warningMessage = warning;
      }

      // Add useful feedback as needed.
      this.detectSpecialUploadableFormats(
        data.img.naturalWidth,
        data.img.naturalHeight
      );

      // Augment file object with dimensions so we can access that
      // information e.g. to check for valid bundling of images.
      data.file.contentWidth = data.img.naturalWidth;
      data.file.contentHeight = data.img.naturalHeight;

      this.augmentContainDimensions(data.file);
    },
    augmentContainDimensions(file) {
      file.containDimensions = this.calculateContainDimensions(
        // Source
        file.contentWidth,
        file.contentHeight,
        // Target: These match the .mini-preview dimensions.
        360,
        202
      );
    },
    /**
     *  Append info message and remember the detected format.
     *
     *  Remembering that is important hide/show UI.
     */
    detectSpecialUploadableFormats(width, height) {
      this.isConvertibleMultiscreen_ = false;
      this.isSpecialLedWallFormat_ = false;

      if (this.isConvertibleMultiscreen(width, height)) {
        this.isConvertibleMultiscreen_ = true;
        this.infoMessage +=
          this.$t('videoFormatIsConvertibleMultiscreen') + '\n';
      } else if (this.isSpecialLedWallFormat(width, height)) {
        this.isSpecialLedWallFormat_ = true;
        this.infoMessage += this.$t('videoFormatIsSpecialLedWallFormat') + '\n';
      }
    },
    getVideoObject() {
      return {video: this.$refs.videopreview, file: this.file};
    },
    setInitialPlayheadPosition(videoElement) {
      if (!videoElement || isNaN(videoElement.duration)) {
        return;
      }
      // Move playhead to the middle for better initial preview.
      videoElement.currentTime = videoElement.duration / 2;
    },
    checkVideo(data) {
      if (!data.video) {
        return;
      }

      this.setInitialPlayheadPosition(data.video);

      // Check for errors that would need us to abort here.
      let errorMessage = '';
      const videoMbs = data.file.size / 1000.0 / 1000.0;
      if (videoMbs > MAX_VIDEO_SIZE_MBS) {
        errorMessage += this.$t('videoUploadSizeWarning') + '. ';
      }
      if (errorMessage) {
        this.$emit('remove', data.file);

        this.$bus.$emit('alert-warning', {
          message: errorMessage,
          timeout: 8000,
          translate: false,
        });
      }

      // Check if we need a User decision to continue. If the User
      // has already chosen an output format, skip the warning.
      this.videoHasCorrectAspectRatio = true;
      this.warningMessage = '';
      this.warningMessageOnClick = null;
      this.infoMessage = '';

      // A resolution of 0x0 is a sign for the browser failing to load
      // the video (e.g. when trying to load a video encoded with
      // HEVC/H265 or a m4v stream). In that case we'll skip validations
      // here and hope that the server can handle the file after upload.
      this.previewVideoLoaded =
        data.video.videoWidth != 0 && data.video.videoHeight != 0;

      this.detectSpecialUploadableFormats(
        data.video.videoWidth,
        data.video.videoHeight
      );

      const notSupportedDimension = !this.hasSupportedDimensions(
        data.video.videoWidth,
        data.video.videoHeight
      );

      // Augment file object with dimensions so we can access that
      // information e.g. to check for valid bundling of videos.
      data.file.contentWidth = data.video.videoWidth;
      data.file.contentHeight = data.video.videoHeight;

      this.augmentContainDimensions(data.file);

      const notSpecialUploadableFormat =
        !this.isConvertibleMultiscreen_ && !this.isSpecialLedWallFormat_;

      if (!this.previewVideoLoaded) {
        console.log(
          'Could not load preview video, so force choosing a format.'
        );
      }

      const needsReformatting =
        !this.previewVideoLoaded ||
        (notSupportedDimension && notSpecialUploadableFormat);

      const someFormatAlreadyChosen =
        this.uploadable.chosenTVFormats.length > 0;

      if (needsReformatting && !someFormatAlreadyChosen) {
        this.videoHasCorrectAspectRatio = false;
        this.warningMessage = this.$t('chooseVideoFormatPlease');
        this.warningMessageOnClick = function () {
          this.showVideoFormatChooserDialog = true;
        };
      } else if (someFormatAlreadyChosen) {
        this.infoMessage +=
          this.$t('videoFormatWillConvertTo') +
          ': ' +
          this.uploadable.chosenTVFormats.map((f) => f.name).join(', ') +
          '\n';
      }

      if (needsReformatting && !someFormatAlreadyChosen) {
        // Signal parent component that we want to open the format
        // chooser dialog for this content automatically.
        this.$emit('maybe-show-format-chooser');
      }
    },
    onVideoFormatsChosen(tvformats) {
      this.uploadable.chosenTVFormats = tvformats;
      this.checkVideo(this.getVideoObject());
    },
    getTitleFromFileName(filename) {
      if (!filename) {
        return filename;
      }

      let tokens = filename.split('.');
      tokens = tokens.slice(0, tokens.length - 1);
      let text = tokens.join('.');
      let refined = text.replace(new RegExp('[._]', 'g'), ' ');
      let words = refined.split(' ');
      // Cap to title limit of the model.
      return words.join(' ').slice(0, 128);
    },
    maybeSetTitleFromFileName(filename) {
      if (!this.uploadable.title) {
        this.uploadable.title = this.getTitleFromFileName(filename);
      }
    },
    formatFileSize(fileSize) {
      let kb = fileSize / 1000;
      let mb = kb / 1000;
      let mbClamped = Number(mb.toFixed(1)) > 0;
      let result =
        (mbClamped ? mb : kb).toFixed(1) + ' ' + (mbClamped ? 'MB' : 'KB');
      return result;
    },
    makeVideoFilePreviewUrl(videoFile) {
      return URL.createObjectURL(videoFile);
    },
    createPreview(file) {
      this.resetPreview();

      if (this.uploadable.isImage) {
        let reader = new FileReader();
        reader.onload = (event) => {
          this.previewImage = event.target.result;
        };
        reader.readAsDataURL(file);
      } else if (this.uploadable.isVideo || this.uploadable.isAudio) {
        this.previewUrl = this.makeVideoFilePreviewUrl(file);
      }
    },
    resetPreview() {
      this.previewImage = null;
      this.previewUrl = null;
    },
    startPreview(videoElement) {
      if (this.previewingVideoElement) {
        this.stopPreview(this.previewingVideoElement);
      }
      this.previewingVideoElement = videoElement;
      this.$nextTick(() => {
        if (videoElement) {
          videoElement.pause();
          videoElement.currentTime = 0;
          videoElement.play();
        }
      });
    },
    stopPreview(videoElement) {
      if (this.previewingVideoElement) {
        this.previewingVideoElement = null;
        this.stopPreview(this.previewingVideoElement);
      }
      this.$nextTick(() => {
        if (videoElement) {
          videoElement.pause();
          this.setInitialPlayheadPosition(videoElement);
        }
      });
    },
    togglePreview(videoElement) {
      this.previewingVideoElement == videoElement
        ? this.stopPreview(videoElement)
        : this.startPreview(videoElement);
    },
    onCroppedImageSaved(file) {
      this.$emit('update:file', file);
      this.createPreview(file);

      this.imageHasCorrectAspectRatio = true;
      this.warningMessage = '';
    },
    isFormValid() {
      let valid =
        (this.uploadable.title || '').trim() != '' &&
        (this.uploadable.title || '').length <= this.titleLimit &&
        this.uploadable.categoryId !== null;
      if (this.uploadable.isImage) {
        const validImageDuration =
          this.uploadable.duration >= this.minImageDuration &&
          this.uploadable.duration <= this.maxImageDuration;
        valid =
          valid &&
          this.imageHasCorrectAspectRatio &&
          !this.imageIsTooLarge &&
          validImageDuration;
      } else if (this.uploadable.isVideo) {
        valid = valid && this.videoHasCorrectAspectRatio;
      }
      return valid;
    },
  },
};
</script>

<style>
.no-details.input-group--disabled {
  padding: 0;
}
.no-details.input-group--disabled label {
  top: 0;
  height: auto;
}
.no-details.input-group--disabled .input-group__input {
  min-height: auto;
}
.no-details.input-group--disabled .input-group__input input {
  height: auto;
}
.no-details.input-group--disabled .input-group__details {
  display: none;
}
</style>

<style scoped>
.content-upload-file-card {
  background-color: rgba(0, 0, 0, 0.04);
}
.content-upload-file-card:hover {
  background-color: rgba(0, 0, 0, 0.08);
}

.preview-container {
  position: relative; /* Allow children to use 'position: absolute' */
}
.mini-preview {
  width: 360px;
  height: 202px;
  object-fit: contain;
}
@media (max-width: 599px) {
  .preview-container.mini-preview {
    width: 300px;
    height: 169px;
  }
  .mini-preview {
    width: 300px;
    height: 169px;
  }
}

.contentdroptarget--hint {
  position: absolute;
  background: rgba(0, 0, 0, 0.66);
  pointer-events: none;
  word-break: break-word;
}

.contentdroptarget.preview-container:not(.dragged)
  :not(.subcontentpreviews).mini-preview.over {
  outline: 5px solid var(--msr-color-primary);
  z-index: 999;
}

.overlayed-user-action-btn {
  z-index: 1; /* Be on top of the content we want to overlay. */
  margin: 0 !important;
  position: absolute;
  top: calc(50% - 32px);
  left: calc(50% - 32px);
}

.pseudo-anchor {
  text-decoration: underline;
  cursor: pointer;
}

.subcontents-list-transition-enter-active {
  transition: all 0.2s ease-in-out;
}
.subcontents-list-transition-enter {
  opacity: 0;
  transform: translateX(50px);
}

.lighter-background-checker-pattern {
  background-repeat: repeat;
  background-size: 16px 16px;
  background-position: 0 0, 8px 8px;
  background-image: linear-gradient(
      45deg,
      #efeded 25%,
      transparent 25%,
      transparent 75%,
      #efeded 75%,
      #efeded
    ),
    linear-gradient(
      45deg,
      #efeded 25%,
      transparent 25%,
      transparent 75%,
      #efeded 75%,
      #efeded
    );
}
</style>



// WEBPACK FOOTER //
// src/components/ContentUploadFile.vue